/**
 * Reply-template token substitution (Iter 114).
 * ---------------------------------------------
 * Replaces `{customer_name}`, `{ticket_no}`, etc. inside a canned reply
 * with values from the current chat/ticket context. Missing values fall
 * back to a sensible neutral placeholder ("there", "—") rather than
 * leaking a raw `{token}` into the composer.
 */

// Every token the picker is allowed to substitute. Keep this list stable
// — admins reference these names in the "Available tokens" helper.
export const TEMPLATE_TOKENS = [
  { key: "customer_name",  label: "Customer name",   example: "Manju S." },
  { key: "customer_phone", label: "Customer phone",  example: "9019069059" },
  { key: "customer_email", label: "Customer email",  example: "manju@example.com" },
  { key: "ticket_no",      label: "Ticket number",   example: "TKT-000123" },
  { key: "agent_name",     label: "Your name (agent)", example: "Priya V." },
  { key: "tenant",         label: "Company / brand", example: "Rescigo Health" },
  { key: "today",          label: "Today's date",    example: "26 Feb 2026" },
  { key: "customer_code",  label: "Customer code",   example: "C-000123" },
  { key: "due_amount",     label: "Outstanding dues", example: "₹1,250.00" },
  { key: "next_pickup_date", label: "Next pickup date", example: "2026-06-21" },
  { key: "request_no",     label: "Next request no", example: "SR2600608" },
  { key: "zone",           label: "Customer zone",   example: "Pettah Zone" },
  { key: "wallet_balance", label: "Wallet balance",  example: "₹500.00" },
];

const _FALLBACK = {
  customer_name: "there",
  customer_phone: "—",
  customer_email: "—",
  ticket_no: "—",
  agent_name: "team",
  tenant: "Rescigo Health",
};

function _todayLabel() {
  try {
    return new Date().toLocaleDateString(undefined,
      { day: "2-digit", month: "short", year: "numeric" });
  } catch { return new Date().toISOString().slice(0, 10); }
}

/**
 * substituteTokens(body, ctx)
 * @param {string} body — raw template text with `{token}` placeholders
 * @param {object} ctx  — context values from the caller
 * @returns {string} body with tokens replaced (missing keys → fallback)
 */
export function substituteTokens(body, ctx = {}) {
  if (!body || typeof body !== "string") return body || "";
  const values = {
    customer_name:  ctx.customer_name  || _FALLBACK.customer_name,
    customer_phone: ctx.customer_phone || _FALLBACK.customer_phone,
    customer_email: ctx.customer_email || _FALLBACK.customer_email,
    ticket_no:      ctx.ticket_no      || _FALLBACK.ticket_no,
    agent_name:     ctx.agent_name     || _FALLBACK.agent_name,
    tenant:         ctx.tenant         || _FALLBACK.tenant,
    today:          ctx.today          || _todayLabel(),
    customer_code:  ctx.customer_code  || "—",
    due_amount:     ctx.due_amount     || "₹0.00",
    next_pickup_date: ctx.next_pickup_date || "your next scheduled day",
    request_no:     ctx.request_no     || "—",
    zone:           ctx.zone           || "your area",
    wallet_balance: ctx.wallet_balance || "₹0.00",
  };
  return body.replace(/\{([a-z_][a-z0-9_]*)\}/gi, (m, key) => {
    const k = key.toLowerCase();
    return values[k] != null ? String(values[k]) : m;
  });
}
